import {faUpload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button} from 'antd';
import {RcFile} from 'antd/lib/upload';
import {useEffect, useState} from 'react';

import {Spinner} from '../../../../../../layout/spinner/spinner';
import {LocalePlural} from '../../../../../../provider/locale/locale-plural';
import {useFormRules} from '../../../../../../service/form/form-rules-hook';
import {useUploadStoryImage} from '../../../../../../service/yandex-stories/yandex-stories-hook';
import {YandexStoryFormType} from '../../../../../../service/yandex-stories/yandex-stories-type';
import {FormInstance} from '../../../../../../typings/antd';
import {classNames} from '../../../../../../util/css';
import {noop} from '../../../../../../util/function';
import {useRefreshId} from '../../../../../../util/hook';
import {FileFormInput} from '../../../../../form/file-upload-item/file-form-input';
import {Text} from '../../../../../text/text';
import {
    YANDEX_STORY__MAX_SLIDES,
    YANDEX_STORY_MAX_IMAGE_HEIGHT,
    YANDEX_STORY_MAX_IMAGE_WIDTH,
    YANDEX_STORY_MIN_IMAGE_HEIGHT,
    YANDEX_STORY_MIN_IMAGE_WIDTH,
} from '../../../yandex-story-form-helper';

import {appendYandexStorySlides, UploadedSlideFilesMapType} from './yandex-story-slide-upload-helper';
import * as styles from './yandex-story-slides-upload-panel.scss';

type PropsType = {
    formInstance: FormInstance<YandexStoryFormType>;
    isUploadBlocked: boolean;
    onUploadingChange: (isUploading: boolean) => void;
};

// Empty state of the story step: takes the place of the slide preview until the first images are
// uploaded, so adding a story never leaves the single screen.
export function YandexStorySlidesUploadPanel(props: PropsType): JSX.Element {
    const {formInstance, isUploadBlocked, onUploadingChange} = props;

    const {mutateAsync: uploadFile} = useUploadStoryImage();
    const {validateImageDimension} = useFormRules();

    const [filesMap, setFilesMap] = useState<UploadedSlideFilesMapType>({});

    // Remounts the input once a batch has been handled, so the next one starts from an empty list
    // instead of re-adding the files this one already turned into slides.
    const {refresh, refreshId} = useRefreshId();

    const uploadingCount = Object.keys(filesMap).length;
    const isUploading = uploadingCount > 0;

    useEffect(() => {
        onUploadingChange(isUploading);
    }, [isUploading, onUploadingChange]);

    // The first batch fills the rail and unmounts this panel in the same commit, before the effect
    // above gets to report the batch as finished. Releasing the lock here covers that.
    useEffect(() => {
        return () => {
            onUploadingChange(false);
        };
    }, [onUploadingChange]);

    useEffect(() => {
        if (isUploading && Object.values(filesMap).every((fileItem) => fileItem.status !== 'uploading')) {
            appendYandexStorySlides(formInstance, filesMap);

            setFilesMap({});
            refresh();
        }
    }, [filesMap, formInstance, isUploading, refresh]);

    return (
        <div
            className={classNames(styles.YandexStorySlidesUploadPanel, {
                [styles.YandexStorySlidesUploadPanel__uploading]: isUploading,
            })}
        >
            {isUploading && (
                <div className={styles.YandexStorySlidesUploadPanel_spinnerWrapper}>
                    <Spinner />
                    <Text>
                        <LocalePlural
                            count={uploadingCount}
                            manyKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__LOADING_IMAGES_MANY"
                            singularKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__LOADING_IMAGES_SINGLE"
                            valueMap={{count: uploadingCount}}
                        />
                    </Text>
                </div>
            )}

            {/* Stays mounted while uploading: unmounting it would strand the in-flight upload, whose
                response updates the state of this very input. */}
            <FileFormInput
                key={refreshId}
                onChange={setFilesMap}
                remove={noop}
                showCustomUploadList={false}
                upload={uploadFile}
                uploadProps={{accept: '.png,.jpg', maxFileSize: 10, maxCount: YANDEX_STORY__MAX_SLIDES}}
                validationRules={[
                    async (file: RcFile) =>
                        validateImageDimension(file, {
                            min: {
                                height: YANDEX_STORY_MIN_IMAGE_HEIGHT,
                                width: YANDEX_STORY_MIN_IMAGE_WIDTH,
                            },
                            max: {
                                height: YANDEX_STORY_MAX_IMAGE_HEIGHT,
                                width: YANDEX_STORY_MAX_IMAGE_WIDTH,
                            },
                        }),
                ]}
                value={filesMap}
            >
                <Button className={styles.YandexStorySlidesUploadPanel_uploadArea} disabled={isUploadBlocked}>
                    <FontAwesomeIcon className={styles.YandexStorySlidesUploadPanel_uploadAreaIcon} icon={faUpload} />
                    <Text block bolder stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_1" />

                    <span className={styles.YandexStorySlidesUploadPanel_rules}>
                        <Text block secondary small stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_2" />
                        <Text
                            block
                            secondary
                            small
                            stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_3"
                            valueMap={{
                                minWidth: YANDEX_STORY_MIN_IMAGE_WIDTH,
                                minHeight: YANDEX_STORY_MIN_IMAGE_HEIGHT,
                                maxWidth: YANDEX_STORY_MAX_IMAGE_WIDTH,
                                maxHeight: YANDEX_STORY_MAX_IMAGE_HEIGHT,
                            }}
                        />
                        <Text block secondary small stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_4" />
                    </span>
                </Button>
            </FileFormInput>
        </div>
    );
}
